/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Config } from '../interfaces/Config';
import { Dictionary } from '../classes/Dictionary';
import { ITelemetryItem } from '@microsoft/applicationinsights-core-js';
import { Languages, Theme, Themes, WindowGlobal } from '../types/globalTypes';
import { config, configFromSession, configFromURL, saveConfig } from '../classes/Config';
import { environment, instrumentationKey, version } from './AppInsights/constants';
import { isNullOrUndefined } from 'utils/collection';

/**
 * Initialise the Arm Design System global settings
 * @param {Config}  configFromUser  The user's config
 */
export const initialise = (configFromUser: Config = {}): void => {
  if (isNullOrUndefined(typeof (window as Window))) {
    return;
  }

  const win = window;
  const doc = win.document;

  // Expose the Arm Design System object globally
  const ArmDesignSystem = ((win as WindowGlobal)['ArmDesignSystem'] = (win as WindowGlobal)['ArmDesignSystem'] || {});

  // Define the ADS config
  const configObj: Config = {
    ...configFromSession(win),
    _persistConfig: false,
    ...ArmDesignSystem.config,
    ...configFromURL(win),
    ...configFromUser,
  };

  // Update the config
  config.reset(configObj);
  if (config.getBoolean('_persistConfig')) {
    saveConfig(win, configObj);
  }

  // Set the language
  const defaultLanguage: string = config.get('language', doc.documentElement?.getAttribute('lang') || Languages.EN);
  config.set('language', defaultLanguage);
  if (!isNullOrUndefined(doc.documentElement)) {
    doc.documentElement.setAttribute('lang', defaultLanguage);
  }

  // Set the theme
  const defaultTheme: Theme = config.get('theme', doc.documentElement?.getAttribute('theme') || Themes.DEFAULT);
  config.set('theme', defaultTheme);
  if (!isNullOrUndefined(doc.documentElement)) {
    doc.documentElement?.setAttribute('theme', defaultTheme);
  }

  // Update the global config object
  if (isNullOrUndefined(win.location?.host)) {
    config.set('_isTesting', true);
  }
  if (config.getBoolean('_isTesting')) {
    config.set('isAnimated', false);
  }

  ArmDesignSystem.config = config;

  // Initialise i18next
  if (!isNullOrUndefined(config.get('i18n'))) {
    void Dictionary.init(config.get('i18n'));
  }

  // Initialise the pipeline
  ArmDesignSystem.pipeline = new Map();

  // Initialise AppInsights
  if (!config.getBoolean('_isTesting')) {
    const appInsights = new ApplicationInsights({
      config: { instrumentationKey, disablePageUnloadEvents: ['unload'] },
    });

    appInsights.loadAppInsights();
    const telemetryInitializer = (item: ITelemetryItem): void => {
      item.data.host = window.location.host;
      item.data.referrer = window.location.href;
      item.data.environment = environment;
      item.data.version = version;
    };
    appInsights.addTelemetryInitializer(telemetryInitializer);
    appInsights.trackPageView({ name: 'ADS installed' });
    (window as WindowGlobal)['AppInsights'] = appInsights;
  }
};

export default initialise;
